import React, { useState } from "react";
import axios from "axios";
import { MyDomain } from "../../App";

export default function BarberSubsMPSubsBlock({ onSuccess, cancelledSub }) {
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [focused, setFocused] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("barber");
    window.location.reload();
  };

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const getCardBrand = () => {
    const num = cardNumber.replace(/\s/g, "");
    if (num.startsWith("4")) return "visa";
    if (num.startsWith("5") || num.startsWith("2")) return "mastercard";
    if (num.startsWith("3")) return "amex";
    return null;
  };

  const formatDate = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  // Format "DD-MM-YYYY" → "April 6, 2026"
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    const [day, month, year] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const displayNumber = cardNumber || "•••• •••• •••• ••••";
  const displayName = cardName || "YOUR NAME";
  const displayExpiry = expiry || "MM/YY";
  const brand = getCardBrand();

  const plans = [
    {
      id: "pro",
      name: "Pro",
      price: "17",
      period: "mo",
      features: [
        "Unlimited clients",
        "Advanced analytics",
        "Support 24/7",
        "Custom branding",
      ],
      accent: "#c8a96e",
      popular: true,
    },
  ];

  const handleSubmit = async () => {
    if (!cardNumber || !cardName || !expiry || !cvv) return;

    setError(null);
    setIsProcessing(true);

    try {
      const barberRaw = localStorage.getItem("barber");
      const barber = barberRaw ? JSON.parse(barberRaw) : null;

      if (!barber) {
        setError("Barber session not found. Please log in again.");
        setIsProcessing(false);
        return;
      }

      const today = new Date();
      const dateEnd = new Date(today);
      dateEnd.setDate(dateEnd.getDate() + 30);

      const plan = plans.find((p) => p.id === selectedPlan);

      const subscriptionPayload = {
        BarberId: barber.id,
        CountryNumber: barber.countryNumber,
        BarberPhoneNumber: barber.phoneNumber,
        Status: 1,
        DateStart: formatDate(today),
        DateEnd: formatDate(dateEnd),
        DatePay: formatDate(today),
        Price: plan.price,
        ThePlan: 5,
        NumberOfMonths: 1,
        TemporaryOrPermanent: 2,
      };

      await axios.post(
        `${MyDomain}Subscription/CreateSubscription`,
        subscriptionPayload,
      );

      setIsProcessing(false);
      setSuccess(true);
      if (onSuccess) onSuccess(); // re-check subscription in parent
    } catch (err) {
      setIsProcessing(false);
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Something went wrong. Please try again.",
      );
    }
  };

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.successWrap}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>You're all set!</h2>
          <p style={styles.successSub}>
            Your{" "}
            <strong>{plans.find((p) => p.id === selectedPlan)?.name}</strong>{" "}
            plan is now active.
          </p>
          <button style={styles.successBtn} onClick={() => setSuccess(false)}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>✦</span>
          <span style={styles.logoText}>BARBR</span>
        </div>
        <button style={styles.logoutBtn} onClick={handleLogout}>
          Sign out
        </button>
      </header>

      <main style={styles.main}>
        {/* ── Cancelled subscription notice banner ── */}
        {cancelledSub && (
          <div style={styles.cancelledNoticeBanner}>
            <div style={styles.cancelledNoticeLeft}>
              <span style={styles.cancelledNoticeIcon}>ℹ</span>
            </div>
            <div style={styles.cancelledNoticeBody}>
              <p style={styles.cancelledNoticeTitle}>
                Your subscription was cancelled
              </p>
              <p style={styles.cancelledNoticeText}>
                You still have full access to all Pro features until{" "}
                <span style={styles.cancelledNoticeDate}>
                  {formatDisplayDate(cancelledSub.dateEnd)}
                </span>
                . After that, renew below to keep your shop running.
              </p>
            </div>
            <div style={styles.cancelledNoticeMeta}>
              <p style={styles.cancelledNoticeMetaLabel}>ACCESS ENDS</p>
              <p style={styles.cancelledNoticeMetaValue}>
                {formatDisplayDate(cancelledSub.dateEnd)}
              </p>
              <p style={styles.cancelledNoticeMetaLabel}>LAST PAID</p>
              <p style={styles.cancelledNoticeMetaValue}>
                ${cancelledSub.price}/mo
              </p>
            </div>
          </div>
        )}

        <div style={styles.topText}>
          <p style={styles.eyebrow}>SUBSCRIPTION PLANS</p>
          <h1 style={styles.headline}>
            {cancelledSub ? (
              <>
                Renew Your <em style={styles.em}>Plan</em>
              </>
            ) : (
              <>
                Grow Your <em style={styles.em}>Craft</em>
              </>
            )}
          </h1>
          <p style={styles.subline}>
            {cancelledSub
              ? "Resubscribe to keep full access to your shop."
              : "Choose a plan and start managing your shop like a pro."}
          </p>
        </div>

        {/* Plans */}
        <div style={styles.plansRow}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              style={{
                ...styles.planCard,
                ...(selectedPlan === plan.id
                  ? { ...styles.planCardActive, borderColor: plan.accent }
                  : {}),
              }}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div
                  style={{ ...styles.popularBadge, background: plan.accent }}
                >
                  Most Popular
                </div>
              )}
              <div
                style={{
                  ...styles.planDot,
                  background:
                    selectedPlan === plan.id ? plan.accent : "transparent",
                  borderColor: plan.accent,
                }}
              />
              <p style={styles.planName}>{plan.name}</p>
              <div style={styles.planPrice}>
                <span style={styles.planCurrency}>$</span>
                <span
                  style={{
                    ...styles.planAmount,
                    color: selectedPlan === plan.id ? plan.accent : "#e8ddd0",
                  }}
                >
                  {plan.price}
                </span>
                <span style={styles.planPeriod}>/{plan.period}</span>
              </div>
              <ul style={styles.planFeatures}>
                {plan.features.map((f, i) => (
                  <li key={i} style={styles.planFeature}>
                    <span style={{ ...styles.checkmark, color: plan.accent }}>
                      ✓
                    </span>{" "}
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Payment Section */}
        <div style={styles.paymentSection}>
          {/* Card Visual */}
          <div style={styles.cardScene}>
            <div
              style={{
                ...styles.cardVisual,
                transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
              }}
            >
              {/* Front */}
              <div style={styles.cardFront}>
                <div style={styles.cardTopRow}>
                  <div style={styles.cardChip}>
                    <div style={styles.chipLine} />
                    <div style={styles.chipLine} />
                  </div>
                  {brand === "visa" && (
                    <span style={styles.cardBrandText}>VISA</span>
                  )}
                  {brand === "mastercard" && (
                    <div style={styles.mcLogo}>
                      <div
                        style={{
                          ...styles.mcCircle,
                          background: "#eb001b",
                          marginRight: "-8px",
                        }}
                      />
                      <div
                        style={{ ...styles.mcCircle, background: "#f79e1b" }}
                      />
                    </div>
                  )}
                  {brand === "amex" && (
                    <span style={styles.cardBrandText}>AMEX</span>
                  )}
                </div>
                <p style={styles.cardNumberDisplay}>{displayNumber}</p>
                <div style={styles.cardBottomRow}>
                  <div>
                    <p style={styles.cardLabel}>CARD HOLDER</p>
                    <p style={styles.cardValue}>{displayName.toUpperCase()}</p>
                  </div>
                  <div>
                    <p style={styles.cardLabel}>EXPIRES</p>
                    <p style={styles.cardValue}>{displayExpiry}</p>
                  </div>
                </div>
              </div>
              {/* Back */}
              <div style={styles.cardBack}>
                <div style={styles.cardStripe} />
                <div style={styles.cardCvvRow}>
                  <p style={styles.cardLabel}>CVV</p>
                  <div style={styles.cvvBox}>
                    {cvv ? cvv.replace(/./g, "•") : "•••"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div style={styles.form}>
            <p style={styles.formTitle}>Payment Details</p>

            <div style={styles.fieldWrap}>
              <label style={styles.label}>Card Number</label>
              <div
                style={{
                  ...styles.inputWrap,
                  ...(focused === "number" ? styles.inputFocused : {}),
                }}
              >
                <input
                  style={styles.input}
                  placeholder="0000 0000 0000 0000"
                  value={cardNumber}
                  onChange={(e) =>
                    setCardNumber(formatCardNumber(e.target.value))
                  }
                  onFocus={() => {
                    setFocused("number");
                    setIsFlipped(false);
                  }}
                  onBlur={() => setFocused(null)}
                  maxLength={19}
                />
              </div>
            </div>

            <div style={styles.fieldWrap}>
              <label style={styles.label}>Cardholder Name</label>
              <div
                style={{
                  ...styles.inputWrap,
                  ...(focused === "name" ? styles.inputFocused : {}),
                }}
              >
                <input
                  style={styles.input}
                  placeholder="John Smith"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  onFocus={() => {
                    setFocused("name");
                    setIsFlipped(false);
                  }}
                  onBlur={() => setFocused(null)}
                />
              </div>
            </div>

            <div style={styles.twoCol}>
              <div style={styles.fieldWrap}>
                <label style={styles.label}>Expiry Date</label>
                <div
                  style={{
                    ...styles.inputWrap,
                    ...(focused === "expiry" ? styles.inputFocused : {}),
                  }}
                >
                  <input
                    style={styles.input}
                    placeholder="MM/YY"
                    value={expiry}
                    onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    onFocus={() => {
                      setFocused("expiry");
                      setIsFlipped(false);
                    }}
                    onBlur={() => setFocused(null)}
                    maxLength={5}
                  />
                </div>
              </div>
              <div style={styles.fieldWrap}>
                <label style={styles.label}>CVV</label>
                <div
                  style={{
                    ...styles.inputWrap,
                    ...(focused === "cvv" ? styles.inputFocused : {}),
                  }}
                >
                  <input
                    style={styles.input}
                    placeholder="•••"
                    value={cvv}
                    onChange={(e) =>
                      setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    onFocus={() => {
                      setFocused("cvv");
                      setIsFlipped(true);
                    }}
                    onBlur={() => {
                      setFocused(null);
                      setIsFlipped(false);
                    }}
                    maxLength={4}
                    type="password"
                  />
                </div>
              </div>
            </div>

            <div style={styles.summaryBox}>
              <span style={styles.summaryLabel}>
                {plans.find((p) => p.id === selectedPlan)?.name} Plan — billed
                monthly
              </span>
              <span style={styles.summaryAmount}>
                ${plans.find((p) => p.id === selectedPlan)?.price}/mo
              </span>
            </div>

            {error && <div style={styles.errorBox}>⚠ {error}</div>}

            <button
              style={{
                ...styles.payBtn,
                ...(isProcessing ? styles.payBtnProcessing : {}),
              }}
              onClick={handleSubmit}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <span>◌ Processing…</span>
              ) : (
                `${cancelledSub ? "Resubscribe" : "Subscribe"} · $${plans.find((p) => p.id === selectedPlan)?.price}/mo`
              )}
            </button>

            <p style={styles.secureNote}>
              🔒 Secured with 256-bit SSL encryption. Cancel anytime.
            </p>
          </div>
        </div>
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@300;400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0f0d0a; }
        input::placeholder { color: #5a5048; }
        input { outline: none; background: transparent; border: none; width: 100%; color: #e8ddd0; font-family: 'DM Mono', monospace; font-size: 15px; letter-spacing: 0.05em; }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f0d0a",
    color: "#e8ddd0",
    fontFamily: "'DM Mono', monospace",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid #2a2520",
  },
  logo: { display: "flex", alignItems: "center", gap: "10px" },
  logoIcon: { color: "#c8a96e", fontSize: "18px" },
  logoText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "22px",
    fontWeight: 600,
    letterSpacing: "0.3em",
    color: "#e8ddd0",
  },
  logoutBtn: {
    background: "transparent",
    border: "1px solid #3a3028",
    color: "#8a7a6a",
    padding: "8px 18px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "12px",
    letterSpacing: "0.05em",
    transition: "all 0.2s",
  },
  main: { maxWidth: "1100px", margin: "0 auto", padding: "40px 30px 60px" },

  // ── Cancelled notice banner ──────────────────────────────────────────────
  cancelledNoticeBanner: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    background: "rgba(200,169,110,0.06)",
    border: "1px solid rgba(200,169,110,0.25)",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "40px",
    flexWrap: "wrap",
  },
  cancelledNoticeLeft: { flexShrink: 0, paddingTop: "2px" },
  cancelledNoticeIcon: { fontSize: "18px", color: "#c8a96e" },
  cancelledNoticeBody: { flex: 1, minWidth: "200px" },
  cancelledNoticeTitle: {
    fontSize: "12px",
    letterSpacing: "0.1em",
    color: "#c8a96e",
    marginBottom: "6px",
    fontWeight: 600,
  },
  cancelledNoticeText: {
    fontSize: "12px",
    color: "#7a6a5a",
    lineHeight: 1.7,
  },
  cancelledNoticeDate: {
    color: "#c8a96e",
    fontWeight: 600,
  },
  cancelledNoticeMeta: {
    flexShrink: 0,
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  cancelledNoticeMetaLabel: {
    fontSize: "8px",
    letterSpacing: "0.2em",
    color: "#5a5048",
  },
  cancelledNoticeMetaValue: {
    fontSize: "13px",
    color: "#e8ddd0",
    marginBottom: "4px",
  },

  topText: { textAlign: "center", marginBottom: "52px" },
  eyebrow: {
    fontSize: "11px",
    letterSpacing: "0.3em",
    color: "#c8a96e",
    marginBottom: "12px",
  },
  headline: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "52px",
    fontWeight: 400,
    lineHeight: 1.1,
    marginBottom: "14px",
    color: "#f0e8dc",
  },
  em: { fontStyle: "italic", color: "#c8a96e" },
  subline: { color: "#7a6a5a", fontSize: "14px", letterSpacing: "0.03em" },
  plansRow: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    marginBottom: "60px",
    flexWrap: "wrap",
  },
  planCard: {
    background: "#17140f",
    border: "1px solid #2a2520",
    borderRadius: "12px",
    padding: "28px 24px",
    width: "220px",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.25s",
  },
  planCardActive: { background: "#1e1a13", borderWidth: "2px" },
  popularBadge: {
    position: "absolute",
    top: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "4px 14px",
    borderRadius: "20px",
    fontSize: "10px",
    letterSpacing: "0.1em",
    color: "#0f0d0a",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  planDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    border: "2px solid",
    marginBottom: "14px",
    transition: "all 0.2s",
  },
  planName: {
    fontSize: "11px",
    letterSpacing: "0.2em",
    color: "#8a7a6a",
    marginBottom: "8px",
  },
  planPrice: {
    display: "flex",
    alignItems: "baseline",
    gap: "2px",
    marginBottom: "18px",
  },
  planCurrency: { fontSize: "16px", color: "#7a6a5a" },
  planAmount: {
    fontSize: "38px",
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 600,
    lineHeight: 1,
    transition: "color 0.2s",
  },
  planPeriod: { fontSize: "12px", color: "#5a5048" },
  planFeatures: { listStyle: "none" },
  planFeature: {
    fontSize: "12px",
    color: "#7a6a5a",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  checkmark: { fontSize: "11px", fontWeight: 700 },
  paymentSection: {
    display: "flex",
    gap: "60px",
    alignItems: "flex-start",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  cardScene: { perspective: "1000px", flexShrink: 0 },
  cardVisual: {
    width: "340px",
    height: "200px",
    position: "relative",
    transformStyle: "preserve-3d",
    transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  cardFront: {
    position: "absolute",
    inset: 0,
    backfaceVisibility: "hidden",
    background:
      "linear-gradient(135deg, #2a2218 0%, #1a160f 50%, #251e14 100%)",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #3a3028",
    boxShadow:
      "0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(200,169,110,0.15)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  cardBack: {
    position: "absolute",
    inset: 0,
    backfaceVisibility: "hidden",
    background: "linear-gradient(135deg, #1e1a10 0%, #1a160f 100%)",
    borderRadius: "16px",
    transform: "rotateY(180deg)",
    border: "1px solid #3a3028",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  cardStripe: { height: "48px", background: "#0a0806", marginBottom: "20px" },
  cardCvvRow: {
    padding: "0 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    justifyContent: "flex-end",
  },
  cvvBox: {
    background: "#e8ddd0",
    color: "#0f0d0a",
    padding: "6px 16px",
    borderRadius: "4px",
    fontFamily: "'DM Mono', monospace",
    fontSize: "14px",
    letterSpacing: "0.1em",
    minWidth: "60px",
    textAlign: "center",
  },
  cardTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardChip: {
    width: "36px",
    height: "28px",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    borderRadius: "6px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
    padding: "6px",
  },
  chipLine: { width: "100%", height: "1px", background: "rgba(0,0,0,0.3)" },
  cardBrandText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "20px",
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: "#e8ddd0",
    fontStyle: "italic",
  },
  mcLogo: { display: "flex", alignItems: "center" },
  mcCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    opacity: 0.9,
  },
  cardNumberDisplay: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "17px",
    letterSpacing: "0.2em",
    color: "#e8ddd0",
    textAlign: "center",
  },
  cardBottomRow: { display: "flex", justifyContent: "space-between" },
  cardLabel: {
    fontSize: "8px",
    letterSpacing: "0.15em",
    color: "#7a6a5a",
    marginBottom: "3px",
  },
  cardValue: { fontSize: "13px", letterSpacing: "0.05em", color: "#e8ddd0" },
  form: { width: "360px", maxWidth: "100%" },
  formTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "24px",
    fontWeight: 400,
    color: "#e8ddd0",
    marginBottom: "28px",
  },
  fieldWrap: { marginBottom: "18px" },
  label: {
    display: "block",
    fontSize: "10px",
    letterSpacing: "0.15em",
    color: "#7a6a5a",
    marginBottom: "8px",
  },
  inputWrap: {
    border: "1px solid #2a2520",
    borderRadius: "8px",
    padding: "13px 16px",
    background: "#17140f",
    transition: "border-color 0.2s",
  },
  inputFocused: { borderColor: "#c8a96e" },
  twoCol: { display: "flex", gap: "14px" },
  summaryBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#17140f",
    border: "1px solid #2a2520",
    borderRadius: "8px",
    padding: "14px 16px",
    marginBottom: "20px",
    marginTop: "6px",
  },
  summaryLabel: { fontSize: "12px", color: "#7a6a5a" },
  summaryAmount: {
    fontSize: "16px",
    color: "#c8a96e",
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 600,
  },
  errorBox: {
    background: "rgba(180, 60, 40, 0.1)",
    border: "1px solid rgba(180, 60, 40, 0.3)",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "16px",
    fontSize: "12px",
    color: "#d07060",
    letterSpacing: "0.03em",
  },
  payBtn: {
    width: "100%",
    padding: "16px",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    border: "none",
    borderRadius: "8px",
    color: "#0f0d0a",
    fontFamily: "'DM Mono', monospace",
    fontSize: "14px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    cursor: "pointer",
    transition: "all 0.2s",
    marginBottom: "14px",
  },
  payBtnProcessing: { opacity: 0.7, cursor: "not-allowed" },
  secureNote: {
    textAlign: "center",
    fontSize: "11px",
    color: "#5a5048",
    letterSpacing: "0.03em",
  },
  successWrap: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
  },
  successIcon: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "30px",
    color: "#0f0d0a",
  },
  successTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "40px",
    color: "#e8ddd0",
    fontWeight: 400,
  },
  successSub: { color: "#7a6a5a", fontSize: "14px" },
  successBtn: {
    marginTop: "10px",
    padding: "14px 36px",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    border: "none",
    borderRadius: "8px",
    color: "#0f0d0a",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.08em",
  },
};
